import { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { getAllEmployees, createEmployee, setEmployeeActive, updateEmployee } from "../../lib/api";
import type { Employee } from "../../lib/types";

const ID_REGEX = /^[A-Za-z0-9_.:-]{1,64}$/;

export function EmployeesTab() {
  const { t, reloadEmployees, retail } = useAuth();
  const [list, setList] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function reload() {
    setLoading(true);
    try {
      const { employees } = await getAllEmployees();
      setList(employees);
      await reloadEmployees();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { reload(); }, []);

  async function submit() {
    setError(null);
    if (!ID_REGEX.test(id.trim())) { setError(t("employees_form_invalid_id")); return; }
    if (!name.trim()) { setError(t("employees_form_need_name")); return; }
    setSubmitting(true);
    try {
      await createEmployee(id.trim(), name.trim());
      setId(""); setName("");
      await reload();
    } catch (e: any) {
      if (e.message === "ID_ALREADY_EXISTS") setError(t("employees_form_dup_id"));
      else if (e.message === "NAME_ALREADY_EXISTS") setError(t("employees_form_dup_name"));
      else setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  // Правка карточки: имя и/или ID (смена ID переносит доступы и месяцы на сервере).
  const [editing, setEditing] = useState<Employee | null>(null);
  const [editId, setEditId] = useState("");
  const [editName, setEditName] = useState("");
  const [editViewStore, setEditViewStore] = useState<string>("");
  // Подписи магазинов: из данных месяца, если загружены; иначе ключи как есть.
  const STORE_KEYS = ["Lavina", "Киев"] as const;
  const storeLabel = (key: string) => retail?.stores.find(s => s.store === key)?.label ?? (key === "Киев" ? "Васильковская" : key);
  const [editError, setEditError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function startEdit(e: Employee) {
    setEditing(e); setEditId(e.person_id); setEditName(e.display_name); setEditViewStore(e.view_store ?? ""); setEditError(null);
  }

  async function saveEdit() {
    if (!editing) return;
    setEditError(null);
    const newId = editId.trim();
    const newName = editName.trim();
    if (!ID_REGEX.test(newId)) { setEditError(t("employees_form_invalid_id")); return; }
    if (!newName) { setEditError(t("employees_form_need_name")); return; }
    const patch: { person_id?: string; display_name?: string; view_store?: string | null } = {};
    if (newId !== editing.person_id) patch.person_id = newId;
    if (newName !== editing.display_name) patch.display_name = newName;
    if ((editViewStore || null) !== (editing.view_store ?? null)) patch.view_store = editViewStore || null;
    if (!patch.person_id && !patch.display_name && patch.view_store === undefined) { setEditing(null); return; }
    setSaving(true);
    try {
      await updateEmployee(editing.person_id, patch);
      setEditing(null);
      await reload();
    } catch (err: any) {
      if (err.message === "ID_ALREADY_EXISTS") setEditError(t("employees_form_dup_id"));
      else if (err.message === "NAME_ALREADY_EXISTS") setEditError(t("employees_form_dup_name"));
      else setEditError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function toggle(e: Employee) {
    if (e.active === 1 && !window.confirm(t("employees_archive_confirm", { name: e.display_name }))) return;
    try {
      await setEmployeeActive(e.person_id, e.active !== 1);
      await reload();
    } catch (err: any) {
      setError(err.message);
    }
  }

  return (
    <section className="p-4 space-y-4">
      <div className="bg-white rounded-2xl p-4 border border-slate-200 space-y-3">
        <div className="text-sm font-semibold text-slate-800">{t("employees_add_title")}</div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">{t("employees_form_id")}</label>
          <input
            type="text"
            value={id}
            onChange={(e) => setId(e.target.value)}
            placeholder="42"
            className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-900"
          />
        </div>
        <div>
          <label className="text-xs text-slate-500 block mb-1">{t("employees_form_name")}</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Іванова Анна"
            className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-900"
          />
        </div>
        {error && <div className="text-xs text-rose-600">{error}</div>}
        <button
          onClick={submit}
          disabled={submitting}
          className="w-full bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white text-sm font-semibold rounded-xl px-4 py-2.5 transition"
        >{t("employees_form_submit")}</button>
      </div>

      <div className="bg-white rounded-2xl p-4 border border-slate-200">
        <div className="text-sm font-semibold text-slate-800 mb-3">{t("employees_list_title")}</div>
        {loading && <div className="text-xs text-slate-500">…</div>}
        {!loading && list.length === 0 && <div className="text-xs text-slate-500">{t("employees_empty")}</div>}
        {list.length > 0 && (
          <ul className="space-y-2">
            {list.map(e => (
              <li key={e.person_id} className="text-sm border-b border-slate-100 pb-2 last:border-0">
                {editing?.person_id === e.person_id ? (
                  <div className="space-y-2">
                    <div>
                      <label className="text-xs text-slate-500 block mb-1">{t("employees_form_name")}</label>
                      <input
                        type="text"
                        value={editName}
                        onChange={(ev) => setEditName(ev.target.value)}
                        className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-900"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 block mb-1">{t("employees_form_id")}</label>
                      <input
                        type="text"
                        value={editId}
                        onChange={(ev) => setEditId(ev.target.value)}
                        className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-900"
                      />
                      <div className="text-[11px] text-slate-500 mt-1">{t("employees_edit_id_note")}</div>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 block mb-1">{t("employees_form_view_store")}</label>
                      <select
                        value={editViewStore}
                        onChange={(ev) => setEditViewStore(ev.target.value)}
                        className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-900"
                      >
                        <option value="">{t("employees_view_store_none")}</option>
                        {STORE_KEYS.map(k => <option key={k} value={k}>{storeLabel(k)}</option>)}
                      </select>
                      <div className="text-[11px] text-slate-500 mt-1">{t("employees_view_store_note")}</div>
                    </div>
                    {editError && <div className="text-xs text-rose-600">{editError}</div>}
                    <div className="flex gap-2">
                      <button
                        onClick={saveEdit}
                        disabled={saving}
                        className="flex-1 bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white text-xs font-semibold rounded-lg px-3 py-2 transition"
                      >{t("employees_edit_save")}</button>
                      <button
                        onClick={() => setEditing(null)}
                        disabled={saving}
                        className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-lg px-3 py-2 transition"
                      >{t("employees_edit_cancel")}</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className={`font-medium truncate ${e.active ? "text-slate-900" : "text-slate-400 line-through"}`}>{e.display_name}</div>
                      <div className="text-[11px] text-slate-500">id: <code>{e.person_id}</code> {!e.active && <span className="text-rose-500 ml-1">{t("employees_archived_label")}</span>}</div>
                      {e.view_store && <div className="text-[11px] text-teal-700">{t("employees_view_store_label", { label: storeLabel(e.view_store) })}</div>}
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      <button
                        onClick={() => startEdit(e)}
                        className="text-xs font-semibold rounded-lg px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700"
                      >{t("employees_edit")}</button>
                      <button
                        onClick={() => toggle(e)}
                        className={`text-xs font-semibold rounded-lg px-3 py-1.5 ${e.active ? "bg-amber-100 hover:bg-amber-200 text-amber-800" : "bg-emerald-100 hover:bg-emerald-200 text-emerald-800"}`}
                      >{e.active ? t("employees_archive") : t("employees_restore")}</button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
